import { inject, NgModule, provideAppInitializer } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OktaAuthModule, OKTA_CONFIG } from '@okta/okta-angular';
import { OktaWrapGuard } from './guards/okta-wrap.guard';
import { TcpUserResolver } from './services/tcp-user-resolver';
import { PermissionsOktaWrapGuard } from './guards/permissions-okta-wrap.guard';
import { OktaTokenInterceptor } from './services/okta-token.interceptor';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { OktaAuth } from '@okta/okta-auth-js';
import { BASE_APP_HREF_FOR_OKTA } from './utils/okta-utils';
import { PRE_BOOTSTRAP_USER_INFO } from './utils/pre-bootstrap-config-loader.util';
import { getOktaConfigForApp } from './utils/okta-config-provider.util';
import { getEnvironmentFromLocation } from './utils/environment-detector.util';
import { Store } from '@ngrx/store';
import { authActions } from '@content-platform/web-angular-shared-util-auth';
import * as i0 from "@angular/core";
/**
 * Factory function for app initializer that dispatches pre-bootstrap user info to the NgRx store.
 * This runs BEFORE effects initialize, ensuring user info (cip_oid) is available for GraphQL calls.
 *
 * @param store - NgRx store instance
 * @param userInfo - User info from pre-bootstrap (optional - only available for migrated apps)
 * @returns Factory function that dispatches user info to store
 */
export function initializePreBootstrapUserInfo(store, userInfo) {
    return () => {
        if (userInfo) {
            console.log('🚀 App initializer (shared module): Dispatching pre-bootstrap user info to store:', userInfo?.email);
            store.dispatch(new authActions.SetOktaUserclaim(userInfo));
            store.dispatch(new authActions.SetIsAuthenticated(true));
        }
        // Return resolved promise - app initializer expects a Promise or Observable
        return Promise.resolve();
    };
}
export class WebAngularSharedDataAccessOktaAuth2Module {
    static forRoot(base_href) {
        // Build OktaAuthOptions via the shared utility — same factory used by the
        // pre-bootstrap flow (main.ts → authenticateAndLoadConfigsBeforeBootstrap).
        // Single source of truth for Okta config (redirectUri, postLogoutRedirectUri,
        // restoreOriginalUri, PKCE, etc.) avoids drift between bootstrap-time and
        // runtime instances. The runtime OktaAuth created here primarily reads/refreshes
        // tokens already established by the pre-bootstrap login flow.
        // tslint:disable-next-line: no-shadowed-variable
        const oktaAuth = new OktaAuth(getOktaConfigForApp(getEnvironmentFromLocation(), base_href));
        const oktaConfig = { oktaAuth };
        return {
            ngModule: WebAngularSharedDataAccessOktaAuth2Module,
            providers: [
                {
                    provide: BASE_APP_HREF_FOR_OKTA,
                    useValue: { base_href }
                },
                // OKTA_CONFIG only: do not provide OKTA_AUTH as useValue; that overrides the OktaAuthModule
                // factory and skips oktaAuth.start(), which breaks OktaAuthGuard / signInWithRedirect.
                {
                    provide: OKTA_CONFIG,
                    useValue: oktaConfig
                },
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: OktaTokenInterceptor,
                    multi: true
                }
            ]
        };
    }
    static { this.ɵfac = function WebAngularSharedDataAccessOktaAuth2Module_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || WebAngularSharedDataAccessOktaAuth2Module)(); }; }
    static { this.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: WebAngularSharedDataAccessOktaAuth2Module }); }
    static { this.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ providers: [
            OktaWrapGuard,
            PermissionsOktaWrapGuard,
            TcpUserResolver,
            // App initializer to dispatch pre-bootstrap user info to NgRx store.
            // userInfo is optional so it works for both migrated and non-migrated apps.
            provideAppInitializer(() => {
                const store = inject(Store);
                const userInfo = inject(PRE_BOOTSTRAP_USER_INFO, { optional: true });
                return initializePreBootstrapUserInfo(store, userInfo)();
            })
        ], imports: [CommonModule,
            OktaAuthModule] }); }
}
//# sourceMappingURL=web-angular-shared-data-access-okta-auth2.module.js.map